import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  X,
  Clock,
  Users,
  Star,
  BookOpen,
  PlayCircle,
  CheckCircle2,
  RotateCcw,
  ExternalLink,
  Heart,
  Sparkles,
  Loader2,
  QrCode,
} from "lucide-react";

import {
  fetchCourseDetails,
  markEnrollmentCompleted,
  reopenEnrollment,
  updateEnrollmentProgress,
  type MyEnrollment,
} from "@/services/courseService";
import { useAuth } from "@/hooks/useAuth";
import { useWishlist } from "@/hooks/useWishlist";
import { getCourseStatus } from "@/utils/courseStatus";
import { toast } from "@/hooks/use-toast";

import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { PixCheckoutDialog } from "@/components/payments/PixCheckoutDialog";

interface Props {
  courseId: string | null;
  enrollment?: MyEnrollment | null;
  onClose: () => void;
}

/**
 * Right-side detail panel that opens inside the dashboard.
 * Mobile/tablet → Sheet from the right.
 * Desktop (lg+) → fixed inline drawer pinned to viewport right.
 */
export function CourseDetailsPanel({ courseId, enrollment, onClose }: Props) {
  const open = Boolean(courseId);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 768px)").matches
      : true,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(min-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  // Sync `?course=<id>` to URL so back/refresh works
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (courseId) url.searchParams.set("course", courseId);
    else url.searchParams.delete("course");
    window.history.replaceState({}, "", url.toString());
  }, [courseId]);

  return (
    <>
      {/* Mobile / tablet — sheet. Only mounted below lg, otherwise the Radix
          overlay (rendered in a portal) covers the whole page even when
          SheetContent is hidden via lg:hidden. */}
      {!isDesktop && (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
          <SheetContent
            side="right"
            className="w-full sm:max-w-md border-white/10 bg-card/95 backdrop-blur-2xl p-0"
          >
            <PanelBody courseId={courseId} enrollment={enrollment} onClose={onClose} />
          </SheetContent>
        </Sheet>
      )}

      {/* Desktop inline drawer with click-outside backdrop */}
      {isDesktop && open && (
        <>
          <div
            className="fixed inset-0 z-30 bg-background/40 backdrop-blur-[2px] animate-in fade-in duration-150"
            onClick={onClose}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
            }}
            className="fixed right-0 top-0 z-40 h-screen w-[min(640px,92vw)] lg:w-[520px] xl:w-[600px] border-l border-white/10 bg-card/95 backdrop-blur-2xl shadow-[0_0_60px_-10px_hsl(var(--primary)/0.25)] animate-in slide-in-from-right duration-200"
          >
            <PanelBody courseId={courseId} enrollment={enrollment} onClose={onClose} />
          </div>
        </>
      )}
    </>
  );
}

/* ─────────────────────────  Panel body  ───────────────────────── */

function PanelBody({ courseId, enrollment, onClose }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isSaved, toggle: toggleWishlist } = useWishlist();

  const { data, isLoading } = useQuery({
    queryKey: ["course-details", courseId],
    queryFn: () => fetchCourseDetails(courseId!),
    enabled: Boolean(courseId),
    staleTime: 60_000,
  });

  const [progressDraft, setProgressDraft] = useState<number>(enrollment?.progress ?? 0);
  const [pixOpen, setPixOpen] = useState(false);
  useEffect(() => {
    setProgressDraft(enrollment?.progress ?? 0);
  }, [enrollment?.id, enrollment?.progress]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["my-enrollments", user?.id] });
  };

  const updateMut = useMutation({
    mutationFn: (p: number) => updateEnrollmentProgress(enrollment!.id, p),
    onSuccess: () => {
      toast({ title: t("myCourses.progress.updated"), duration: 1500 });
      invalidate();
    },
    onError: () =>
      toast({ title: t("myCourses.progress.updateFailed"), variant: "destructive" }),
  });

  const completeMut = useMutation({
    mutationFn: () => markEnrollmentCompleted(enrollment!.id),
    onSuccess: () => {
      toast({ title: t("myCourses.progress.completedToast"), duration: 1800 });
      invalidate();
    },
    onError: () =>
      toast({ title: t("myCourses.progress.updateFailed"), variant: "destructive" }),
  });

  const reopenMut = useMutation({
    mutationFn: () => reopenEnrollment(enrollment!.id),
    onSuccess: () => {
      toast({ title: t("myCourses.progress.reopened"), duration: 1500 });
      invalidate();
    },
    onError: () =>
      toast({ title: t("myCourses.progress.updateFailed"), variant: "destructive" }),
  });

  const status = useMemo(
    () => (data ? getCourseStatus(data as any) : null),
    [data]
  );
  const isComingSoon = status === "coming_soon";
  const isCompleted =
    enrollment && (enrollment.progress >= 100 || Boolean(enrollment.completed_at));
  const saved = data ? isSaved(data.id) : false;

  return (
    <>
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
        <div className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-primary">
          <Sparkles className="h-3 w-3" />
          {enrollment ? t("myCourses.panel.enrolled") : t("myCourses.panel.preview")}
        </div>
        <div className="flex items-center gap-1">
          {data?.slug && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 px-2 text-[11px] text-foreground/70 hover:text-foreground"
              asChild
            >
              <Link
                to={`/courses/${data.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("myCourses.panel.openFullPage", { defaultValue: "Open full page" }) as string}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t("myCourses.panel.openFullPage", { defaultValue: "Open full page" })}
                </span>
              </Link>
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        {isLoading || !data ? (
          <div className="space-y-3 p-4">
            <Skeleton className="h-44 w-full rounded-xl" />
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          <>
            {/* Hero image */}
            <div className="relative aspect-[16/9] overflow-hidden">
              <img
                src={data.thumbnail_url}
                alt={data.title}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />
              <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
                {data.category && (
                  <Badge
                    variant="secondary"
                    className="border-white/10 bg-background/70 text-[10px] backdrop-blur-md"
                  >
                    {data.category}
                  </Badge>
                )}
                {data.level && (
                  <Badge
                    variant="outline"
                    className="border-white/15 bg-background/50 text-[10px] backdrop-blur-md"
                  >
                    {data.level}
                  </Badge>
                )}
              </div>
              {!enrollment && user && (
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "absolute right-3 top-3 h-8 w-8 rounded-full backdrop-blur-md",
                    saved
                      ? "bg-primary/30 text-primary hover:bg-primary/40"
                      : "bg-background/60 text-foreground/70 hover:bg-background/80"
                  )}
                  onClick={() => toggleWishlist(data.id)}
                  aria-label="Toggle wishlist"
                >
                  <Heart className={cn("h-4 w-4", saved && "fill-current")} />
                </Button>
              )}
            </div>

            {/* Title & meta */}
            <div className="space-y-3 p-4">
              <div>
                <h2 className="font-serif text-xl leading-tight text-foreground">
                  {data.title}
                </h2>
                <p className="mt-1 text-xs text-foreground/60">{data.instructor_name}</p>
              </div>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-foreground/60">
                {data.duration && (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {data.duration}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <BookOpen className="h-3 w-3" />
                  {data.lessons_count} {t("myCourses.panel.lessons")}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {data.students_count ?? 0}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Star className="h-3 w-3 fill-primary text-primary" />
                  {data.rating ?? 0}
                </span>
              </div>

              {data.description && (
                <p className="text-[13px] leading-relaxed text-foreground/75 whitespace-pre-line">
                  {data.description}
                </p>
              )}

              {/* Enrolled — progress controls */}
              {enrollment && (
                <div className="space-y-3 rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="uppercase tracking-[0.14em] text-foreground/55">
                      {t("myCourses.progress.label")}
                    </span>
                    <span className="tabular-nums font-medium text-foreground/85">
                      {Math.round(progressDraft)}%
                    </span>
                  </div>
                  <Progress value={progressDraft} className="h-1.5" />

                  <div className="space-y-1.5">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-foreground/55">
                      {t("myCourses.progress.update")}
                    </span>
                    <Slider
                      value={[progressDraft]}
                      min={0}
                      max={100}
                      step={5}
                      disabled={Boolean(isCompleted)}
                      onValueChange={(v) => setProgressDraft(v[0] ?? 0)}
                      onValueCommit={(v) => updateMut.mutate(v[0] ?? 0)}
                    />
                  </div>

                  <div className="flex flex-wrap gap-2 pt-1">
                    {!isCompleted ? (
                      <Button
                        size="sm"
                        className="flex-1 gap-1.5"
                        onClick={() => completeMut.mutate()}
                        disabled={completeMut.isPending}
                      >
                        {completeMut.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        )}
                        {t("myCourses.progress.markComplete")}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 gap-1.5"
                        onClick={() => reopenMut.mutate()}
                        disabled={reopenMut.isPending}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        {t("myCourses.progress.reopen")}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer actions */}
      {data && (
        <div className="border-t border-white/5 bg-background/30 p-3 space-y-2">
          {enrollment ? (
            <>
              <Button asChild className="w-full gap-2">
                <Link to={`/learn/${data.slug || data.id}`}>
                  <PlayCircle className="h-4 w-4" />
                  {isCompleted
                    ? t("myCourses.cta.review")
                    : t("myCourses.cta.continue")}
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full gap-2">
                <Link to={`/courses/${data.slug || data.id}`}>
                  <ExternalLink className="h-4 w-4" />
                  {t("myCourses.panel.viewFull")}
                </Link>
              </Button>
            </>
          ) : (
            <>
              {!isComingSoon && (
                <Button asChild className="w-full gap-2">
                  <Link to={`/courses/${data.slug || data.id}`}>
                    <ExternalLink className="h-4 w-4" />
                    {t("myCourses.panel.viewFull")}
                  </Link>
                </Button>
              )}
              {!isComingSoon &&
                !data.is_free &&
                (data as any).accepts_pix !== false &&
                (data.pricing_type === "one_time" ||
                  data.pricing_type === "both" ||
                  !data.pricing_type) && (
                  <Button
                    variant="outline"
                    className="w-full gap-2"
                    onClick={() => {
                      if (!user) {
                        navigate(
                          `/auth?redirect=/courses/${data.slug || data.id}`
                        );
                        return;
                      }
                      setPixOpen(true);
                    }}
                  >
                    <QrCode className="h-4 w-4" />
                    {user
                      ? "Pagar com Pix (BRL)"
                      : t("myCourses.panel.signInToBuy")}
                  </Button>
                )}
              {isComingSoon && (
                <Button disabled className="w-full" variant="outline">
                  {t("courses.comingSoon")}
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
    {data && (
      <PixCheckoutDialog
        open={pixOpen}
        onOpenChange={setPixOpen}
        course={{ id: data.id, title: data.title, slug: data.slug ?? null, price: (data as any).price ?? null }}
      />
    )}
    </>
  );
}
